import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApplicationComment } from "../../services/Application";
import CommunicationPanel from "./CommunicationPanel";
import config from "../../config";

import logger from "../../utils/logger";

let focusBubbleShownKey = null;
/**
 * Komponent wyświetlający przycisk i okno konwersacji dla danego komentarza w aplikacji.
 *
 * @component
 * @param {Object} props
 * @param {string} props.commentId          - Identyfikator komentarza w nowym systemie.
 * @param {string} [props.oldCommentId]     - Identyfikator komentarza w starej bazie (opcjonalnie).
 * @param {string} [props.location]         - Opis lokalizacji komentarza (np. "Sekcja 1-2").
 * @param {boolean} props.isVisible         - Czy w ogóle pokazywać przycisk konwersacji.
 * @param {function(string, function)} props.registerCallback
 *                                            - Funkcja rejestrująca callback do aktualizacji statusu.
 * @param {boolean} [props.viewAll=false]   - Czy pokazywać także komentowane "rozwiązane" lub inne stany.
 *
 * @returns {JSX.Element|null} Przycisk otwierający modal z konwersacją lub `null`.
 */
function CommunicationSpot({
  commentId,
  oldCommentId,
  location,
  isVisible,
  registerCallback,
  viewAll = false,
}) {
  const [color, setColor] = useState("secondary");
  const [trigger, setTrigger] = useState(0);
  const [data, setData] = useState();
  let { step, id } = useParams();
  const routeLocation = useLocation();
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("none");
  const [active, setActive] = useState(false);
  const [conversationModalVisible, setConversationModalVisible] =
    useState(false);
  const [showAttention, setShowAttention] = useState(false);
  const buttonRef = useRef(null);
  const handledFocusRequestRef = useRef(null);

  const debug = config.runmode.debug;

  /**
   * Otwiera okno modalne konwersacji.
   *
   * @function
   */
  const openConversationModal = () => {
    setConversationModalVisible(true);
  };

  /**
   * Obsługuje kliknięcie przycisku otwarcia konwersacji.
   *
   * @function
   */
  const handleClick = () => {
    setTrigger(trigger + 1);
    openConversationModal();
  };

  /**
   * Obsługuje zamknięcie modala konwersacji.
   * Po zamknięciu modala, jeśli konwersacja była "open", zmienia status na "answered".
   *
   * @function
   */
  const handleModalClose = () => {
    if (state === "open") {
      statusHandler("answered");
    }
    setConversationModalVisible(false);
  };

  /**
   * Obsługuje zmianę statusu konwersacji.
   *
   * @function
   * @param {String} newStatus - Nowy status konwersacji.
   * @returns {void}
   */
  const handleStatusChange = (newStatus) => {
    if (debug) logger.log("hSC: newStatus: " + newStatus);

    if (newStatus === "active" || newStatus === "inactive") {
      setActive(newStatus === "active");
      return;
    }

    setState(newStatus);

    switch (newStatus) {
      case "open":
        setColor("danger");
        break;
      case "solved":
        setColor("success");
        break;
      case "answered":
        setColor("warning");
        break;
      default:
        setColor("secondary");
    }
  };

  const [statusHandler, setStatusHandler] = useState(() => handleStatusChange);

  /**
   * Pobiera dane komentarza z serwera i aktualizuje stan komponentu.
   *
   * @function
   */
  const fetchCommentData = async () => {
    if (debug) logger.log("Pobieranie danych komentarza: " + commentId);

    setLoading(true);

    try {
      let commentData = await getApplicationComment(
        id,
        step,
        commentId,
        oldCommentId,
        accessToken,
      );

      if (location) commentData.locationName = location;

      if (debug) logger.log("Pobrano komentarz: ", commentData);

      setData(commentData);
      setState(commentData.status);

      switch (commentData.status) {
        case "open":
          setColor("danger");
          break;
        case "solved":
          setColor("success");
          break;
        case "answered":
          setColor("warning");
          break;
        default:
          setColor("primary");
      }
    } catch (error) {
      logger.error("Error fetching comment data:", error);
    } finally {
      setLoading(false);
      setTrigger(0);
    }
  };

  /**
   * Obsługuje wysłanie wiadomości
   *
   * @function
   */
  const handleMessageSent = async () => {
    await fetchCommentData();
  };

  useEffect(() => {
    fetchCommentData();
  }, [commentId, id, step, accessToken]);

  useEffect(() => {
    if (!loading && debug) {
      logger.log("Pobrano dane komentarza id: " + commentId);
      logger.log(JSON.stringify(data));
    }
  }, [loading, data, commentId, debug]);

  useEffect(() => {
    if (debug) logger.log("Rejestracja callback - id: " + commentId);
    const wrapped = registerCallback
      ? registerCallback(commentId, handleStatusChange)
      : null;

    if (wrapped) {
      setStatusHandler(() => wrapped);
    } else {
      setStatusHandler(() => handleStatusChange);
    }
  }, [commentId, registerCallback, debug]);

  useEffect(() => {
    const params = new URLSearchParams(routeLocation.search);
    const targetCommentId = params.get("comment");
    const focusComments = params.get("focus") === "comments";
    const targetsThisComment =
      targetCommentId === commentId || targetCommentId === oldCommentId;

    if (targetCommentId && !targetsThisComment) return;
    if (!targetCommentId && !focusComments) return;
    if (!["open", "answered", "solved"].includes(state)) return;

    if (targetCommentId) {
      const focusRequest = params.get("focusRequest") || targetCommentId;
      const requestKey = `${id}_${step}_${targetCommentId}_${focusRequest}`;
      if (handledFocusRequestRef.current === requestKey) return;
      handledFocusRequestRef.current = requestKey;
    } else {
      const focusKey = `${id}_${step}`;
      if (focusBubbleShownKey === focusKey) return;
      focusBubbleShownKey = focusKey;
    }

    if (typeof window === "undefined") return;
    setShowAttention(true);
    if (buttonRef.current) {
      buttonRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (targetCommentId) {
      setConversationModalVisible(true);
      setActive(true);
    }
    const timeoutId = setTimeout(() => setShowAttention(false), 6000);
    return () => clearTimeout(timeoutId);
  }, [
    commentId,
    id,
    oldCommentId,
    routeLocation.search,
    state,
    step,
  ]);

  if (
    isVisible &&
    (viewAll || state === "open" || state === "solved" || state === "answered")
  )
    return (
      <div className="ms-auto z-2 d-flex align-items-center">
        {showAttention && (
          <span
            className="rounded d-flex align-items-center justify-content-center h2 px-2 me-1"
            style={{
              pointerEvents: "none",
              backgroundColor: "#5f5f5f",
              color: "#ffffff",
              fontSize: "0.95rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            Kliknij, aby otworzyć konwersację
          </span>
        )}
        {loading ? (
          <button
            ref={buttonRef}
            className={"btn btn-secondary p-0 me-auto w2 h2"}
            title="Ładowanie danych..."
          >
            ...
          </button>
        ) : (
          <button
            ref={buttonRef}
            className={
              "btn tooltip btn-outline-" +
              color +
              " p-0 me-auto w2 h2 border-" +
              color +
              (active ? " border-3" : " border-1")
            }
            onClick={() => {
              handleClick();
              setActive(true);
            }}
            title={
              state === "answered"
                ? "Konwersacja została odpowiedziana"
                : "Kliknij, aby otworzyć konwersację"
            }
          >
            <i className={"bi-envelope fs-3"}></i>
            <span className="tooltiptext tooltip-left">
              {state === "answered"
                ? "Konwersacja została odpowiedziana"
                : "Kliknij, aby otworzyć konwersację"}
            </span>
          </button>
        )}
        {conversationModalVisible &&
  typeof document !== "undefined" &&
  createPortal(
    <>
      <div className="modal-backdrop show"></div>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        tabIndex="-1"
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="small-caps">WIADOMOŚCI</h3>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Zamknij"
                onClick={handleModalClose}
              ></button>
            </div>
            <div className="modal-body text-dark">
              <CommunicationPanel
                data={data}
                isVisible={true}
                embedded={true}
                modal={true}
                onMessageSend={handleMessageSent}
                handleStatusChange={statusHandler}
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={handleModalClose}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )}
      </div>
    );
}

export default CommunicationSpot;


